/**
 * Modal «Entrar como plataforma» (#430): motivo obrigatório → start → dashboard.
 */
import { useEffect, useId, useRef, useState } from 'react'
import { Button, Modal, Textarea, toast } from '../ui/index.js'
import { impersonate } from '../../lib/api.js'
import type { ApiError } from '../../lib/api.js'
import { setToken } from '../../lib/auth.js'
import { setImpersonationSession } from '../../lib/impersonation.js'
import { useI18n } from '../../lib/i18n.js'

export type StartTarget = {
  id: string
  slug: string
  name: string
}

type Props = {
  open: boolean
  restaurant: StartTarget | null
  onClose: () => void
  onStarted: (slug: string) => void
  /** Elemento da árvore que abriu o modal — foco ao cancelar. */
  returnFocusEl?: HTMLElement | null
}

function mapStartError(code: string, message: string, t: (k: string) => string): string {
  if (code === 'ALREADY_IMPERSONATING') return t('admin.impersonation.err_already')
  if (code === 'TENANT_INACTIVE') return t('admin.impersonation.err_inactive')
  if (code === 'VALIDATION_ERROR') return t('admin.impersonation.reason_length')
  return message || t('admin.impersonation.start_err')
}

export function StartImpersonationModal({
  open,
  restaurant,
  onClose,
  onStarted,
  returnFocusEl,
}: Props) {
  const { t } = useI18n()
  const [reason, setReason] = useState('')
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const hintId = useId()
  const reasonRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (open) {
      setReason('')
      setFieldError(null)
      setSaving(false)
      queueMicrotask(() => reasonRef.current?.focus())
    }
  }, [open, restaurant?.id])

  function handleClose() {
    onClose()
    queueMicrotask(() => returnFocusEl?.focus())
  }

  async function handleStart() {
    if (!restaurant) return
    const trimmed = reason.trim()
    if (trimmed.length < 3) {
      setFieldError(
        trimmed.length === 0
          ? t('admin.impersonation.reason_required')
          : t('admin.impersonation.reason_length'),
      )
      return
    }
    if (trimmed.length > 500) {
      setFieldError(t('admin.impersonation.reason_length'))
      return
    }
    setFieldError(null)
    setSaving(true)
    try {
      const data = await impersonate.start({ tenant_id: restaurant.id, reason: trimmed })
      setToken(data.access_token)
      setImpersonationSession({
        tenant_id: data.impersonating.tenant_id,
        tenant_slug: data.impersonating.tenant_slug,
        tenant_name: data.impersonating.tenant_name,
        reason: trimmed,
        expires_at: data.expires_at,
      })
      onClose()
      onStarted(data.impersonating.tenant_slug)
    } catch (err) {
      const apiErr = err as ApiError
      toast.error(mapStartError(apiErr.code ?? 'UNKNOWN', apiErr.message ?? '', t))
    } finally {
      setSaving(false)
    }
  }

  if (!restaurant) return null

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t('admin.impersonation.start_title', { restaurant: restaurant.name })}
      footer={
        <div className="flex gap-3 justify-end">
          <Button variant="secondary" onClick={handleClose} disabled={saving}>
            {t('admin.common.cancel')}
          </Button>
          <Button
            data-testid="impersonation-start"
            loading={saving}
            onClick={() => void handleStart()}
          >
            {t('admin.impersonation.start')}
          </Button>
        </div>
      }
    >
      <div className="space-y-3" data-testid="impersonation-start-modal">
        <Textarea
          ref={reasonRef}
          id="impersonation-reason"
          data-testid="impersonation-reason"
          label={t('admin.impersonation.reason_label')}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value)
            if (fieldError) setFieldError(null)
          }}
          {...(fieldError ? { error: fieldError } : {})}
          aria-describedby={hintId}
        />
        <p id={hintId} className="text-xs text-gray-500">
          {t('admin.impersonation.reason_hint')}
        </p>
      </div>
    </Modal>
  )
}
